/**
 * Admin Service - Client-side administration API
 */

import { apiBase } from "../api-config";

// API base URL (dynamic for dev vs prod)
const API_BASE = apiBase;

// Types for admin API
export interface UserSummary {
  userId: string;
  email: string;
  displayName: string;
  roles: string[];
  isAdmin: boolean;
  isDeleted: boolean;
  createdAt: string;
  lastLoginAt?: string;
  clinicGroupId?: string;
}

class AdminService {
  /**
   * Get all users (admin only)
   */
  async getUsers(): Promise<UserSummary[]> {
    const response = await fetch(`${API_BASE}/api/admin/users`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error("You don't have permission to access admin features");
      }
      throw new Error("Failed to load users");
    }

    return response.json();
  }

  /**
   * Get a specific user (admin only)
   */
  async getUser(userId: string): Promise<UserSummary> {
    const response = await fetch(`${API_BASE}/api/admin/users/${userId}`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to load user");
    }

    return response.json();
  }

  /**
   * Set user admin status (admin only)
   */
  async setAdminStatus(userId: string, isAdmin: boolean): Promise<void> {
    const response = await fetch(
      `${API_BASE}/api/admin/users/${userId}/admin`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ isAdmin }),
      },
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to update admin status");
    }
  }

  /**
   * Set user roles (admin only)
   */
  async setUserRoles(userId: string, roles: string[]): Promise<void> {
    const response = await fetch(
      `${API_BASE}/api/admin/users/${userId}/roles`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ roles }),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to update user roles");
    }
  }

  /**
   * Assign user to a clinic group (admin only)
   */
  async setUserClinic(
    userId: string,
    clinicGroupId: string | null,
  ): Promise<void> {
    const response = await fetch(
      `${API_BASE}/api/admin/users/${userId}/clinic`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ clinicGroupId }),
      },
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to update user clinic");
    }
  }

  /**
   * Delete user (soft delete) (admin only)
   */
  async deleteUser(userId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/api/admin/users/${userId}`, {
      method: "DELETE",
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to delete user");
    }
  }

  /**
   * Restore deleted user (admin only)
   */
  async restoreUser(userId: string): Promise<void> {
    const response = await fetch(
      `${API_BASE}/api/admin/users/${userId}/restore`,
      {
        method: "POST",
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to restore user");
    }
  }

  // =========================================================================
  // Whitelist Management (Beta Feature)
  // =========================================================================

  /**
   * Get whitelist status and all whitelisted emails
   */
  async getWhitelist(): Promise<WhitelistResponse> {
    const response = await fetch(`${API_BASE}/api/admin/whitelist`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to load whitelist");
    }

    return response.json();
  }

  /**
   * Enable or disable whitelist
   */
  async setWhitelistEnabled(enabled: boolean): Promise<void> {
    const response = await fetch(`${API_BASE}/api/admin/whitelist/enabled`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ enabled }),
    });

    if (!response.ok) {
      throw new Error("Failed to update whitelist status");
    }
  }

  /**
   * Add email to whitelist
   */
  async addWhitelistEmail(
    email: string,
    note?: string,
    clinicGroupId?: string,
    preAssignedRole?: string,
  ): Promise<void> {
    const response = await fetch(`${API_BASE}/api/admin/whitelist/emails`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, note, clinicGroupId, preAssignedRole }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to add email to whitelist");
    }
  }

  /**
   * Remove email from whitelist
   */
  async removeWhitelistEmail(email: string): Promise<void> {
    const response = await fetch(
      `${API_BASE}/api/admin/whitelist/emails/${encodeURIComponent(email)}`,
      {
        method: "DELETE",
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to remove email from whitelist");
    }
  }

  // =========================================================================
  // Clinic Group Management
  // =========================================================================

  /**
   * Get all clinic groups
   */
  async getClinicGroups(): Promise<ClinicGroupSummary[]> {
    const response = await fetch(`${API_BASE}/api/admin/clinics`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to load clinic groups");
    }

    return response.json();
  }

  /**
   * Create a new clinic group
   */
  async createClinicGroup(
    name: string,
    description?: string,
  ): Promise<ClinicGroupSummary> {
    const response = await fetch(`${API_BASE}/api/admin/clinics`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ name, description }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to create clinic group");
    }

    return response.json();
  }

  /**
   * Update a clinic group
   */
  async updateClinicGroup(
    id: string,
    name: string,
    description?: string,
    isActive: boolean = true,
  ): Promise<void> {
    const response = await fetch(`${API_BASE}/api/admin/clinics/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ name, description, isActive }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to update clinic group");
    }
  }

  /**
   * Delete a clinic group
   */
  async deleteClinicGroup(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/api/admin/clinics/${id}`, {
      method: "DELETE",
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to delete clinic group");
    }
  }
}

// Whitelist types
export interface WhitelistResponse {
  enabled: boolean;
  emails: WhitelistedEmail[];
}

export interface WhitelistedEmail {
  email: string;
  note?: string;
  addedBy?: string;
  addedAt: string;
  clinicGroupId?: string;
  preAssignedRole?: string;
}

// Clinic Group types
export interface ClinicGroupSummary {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
}

export const adminService = new AdminService();
